import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { money, timeAgo } from "@/lib/format";
import { PageHeader, KpiCard, Loader } from "@/components/common/Bits";
import { StatusBadge } from "@/components/common/StatusBadge";
import {
  IndianRupee,
  ShoppingBag,
  TrendingUp,
  Users,
  LayoutGrid,
  ChefHat,
  AlertTriangle,
  Wallet,
  ArrowRight,
  Receipt,
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const CHART_COLORS = [
  "#EA580C",
  "#16A34A",
  "#0284C7",
  "#F59E0B",
  "#8B5CF6",
  "#DC2626",
];

const Panel = ({ title, subtitle, children, className = "", action }) => (
  <div
    className={`rounded-2xl bg-card border border-border p-5 shadow-[0_2px_8px_rgba(28,25,23,0.04)] ${className}`}
  >
    <div className="mb-4 flex items-start justify-between gap-3">
      <div>
        <h3 className="font-display font-semibold text-foreground">{title}</h3>

        {subtitle && (
          <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
        )}
      </div>

      {action}
    </div>

    {children}
  </div>
);

const EmptyChartState = ({ icon: Icon = Receipt, title, subtitle }) => (
  <div className="h-[260px] flex flex-col items-center justify-center text-center">
    <div className="h-12 w-12 rounded-2xl bg-secondary grid place-items-center mb-3">
      <Icon size={21} className="text-muted-foreground" />
    </div>

    <p className="font-semibold text-sm">{title}</p>

    {subtitle && (
      <p className="text-xs text-muted-foreground mt-1 max-w-[240px]">
        {subtitle}
      </p>
    )}
  </div>
);

const SmallMetric = ({ label, value, icon: Icon, className = "" }) => (
  <div
    className={`rounded-2xl bg-card border border-border p-4 flex items-center gap-3 ${className}`}
  >
    <div className="h-10 w-10 rounded-xl grid place-items-center bg-secondary text-foreground">
      <Icon size={18} />
    </div>

    <div className="min-w-0">
      <p className="font-display text-lg font-bold leading-none truncate">
        {value}
      </p>

      <p className="text-[11px] text-muted-foreground mt-1 truncate">{label}</p>
    </div>
  </div>
);

export default function Dashboard() {
  const { branchId, subscribe } = useApp();
  const { user } = useAuth();
  const [d, setD] = useState(null);

  const isStarter =
    String(user?.plan || user?.subscription?.plan || "").toLowerCase() ===
    "starter";

  const displayOrderType = (type) => {
    const normalized = String(type || "")
      .toLowerCase()
      .replace(/[-\s]/g, "_");

    if (normalized === "dine_in") {
      return isStarter ? "Direct POS Billing" : "Dine-In";
    }

    return String(type || "")
      .replace("_", " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const load = () => {
    api
      .dashboard(branchId)
      .then((r) => setD(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    setD(null);
    load();
  }, [branchId]);

  useEffect(() => {
    const u1 = subscribe("order.new", load);
    const u2 = subscribe("order.update", load);

    return () => {
      u1();
      u2();
    };
  }, [subscribe, branchId]);

  if (!d) {
    return (
      <div className="p-6">
        <Loader label="Loading dashboard" />
      </div>
    );
  }

  const k = d.kpis || {};
  const ops = d.operations || {};
  const cmp = d.comparison || {};

  const salesSeries = Array.isArray(d.sales_series) ? d.sales_series : [];

  const salesByType = Array.isArray(d.sales_by_type) ? d.sales_by_type : [];

  const topItems = Array.isArray(d.top_items) ? d.top_items : [];

  const recentTransactions = Array.isArray(d.recent_transactions)
    ? d.recent_transactions
    : [];

  const lowStockItems = Array.isArray(d.low_stock_items)
    ? d.low_stock_items
    : [];

  const hasSales = Number(k.today_sales || 0) > 0;

  const hasSalesSeries = salesSeries.some(
    (item) => Number(item?.sales || 0) > 0,
  );

  const activeSalesTypes = salesByType.filter(
    (item) => Number(item?.value || 0) > 0,
  );

  const hasTopItems = topItems.some((item) => Number(item?.qty || 0) > 0);

  const weeklySales = Number(cmp?.week?.this || 0);

  const yesterdayComparison = Number(cmp?.today_vs_yesterday?.pct || 0);

  // This customer count represents customers recorded for takeaway/delivery
  // orders, not the restaurant's total customer base.
  const takeawayDeliveryCustomers = Number(k.total_customers || 0);

  const orderTypeCards = [
    {
      label: isStarter ? "Direct POS Billing" : "Dine-In",
      value: money(k.dinein_sales || 0),
      icon: LayoutGrid,
    },
    {
      label: "Takeaway",
      value: money(k.takeaway_sales || 0),
      icon: ShoppingBag,
    },
    {
      label: "Delivery",
      value: money(k.delivery_sales || 0),
      icon: ShoppingBag,
    },
    {
      label: "This Week",
      value: money(weeklySales),
      icon: TrendingUp,
    },
  ];

  return (
    <div className="p-4 sm:p-6 animate-fade-up">
      <PageHeader
        title="Business Snapshot"
        subtitle="What's happening in your restaurant today"
      />

      {/* Pending approvals */}
      {Number(k.pending_approvals || 0) > 0 && (
        <Link
          to="/app/approvals"
          data-testid="dash-approvals-banner"
          className="flex items-center justify-between rounded-2xl border border-amber-200 bg-amber-50 px-5 py-3.5 mb-4 hover:bg-amber-100 transition-colors"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-amber-800">
            <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
            {k.pending_approvals} order
            {k.pending_approvals > 1 ? "s" : ""} awaiting approval before
            reaching the kitchen
          </span>

          <span className="text-sm font-bold text-amber-700">Review →</span>
        </Link>
      )}

      {/* Main KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          testid="kpi-sales"
          label="Today's Sales"
          value={money(k.today_sales || 0)}
          icon={IndianRupee}
          accent="orange"
          delta={yesterdayComparison}
          sub="vs yesterday"
        />

        <KpiCard
          testid="kpi-orders"
          label="Today's Orders"
          value={k.today_orders || 0}
          icon={ShoppingBag}
          accent="sky"
          sub={`${k.pending_orders || 0} active`}
        />

        <KpiCard
          testid="kpi-aov"
          label="Avg Order Value"
          value={money(k.avg_order_value || 0)}
          icon={TrendingUp}
          accent="emerald"
        />

        <KpiCard
          testid="kpi-customers"
          label="Takeaway & Delivery Customers"
          value={takeawayDeliveryCustomers}
          icon={Users}
          accent="violet"
        />
      </div>

      {/* Order type / sales summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        {orderTypeCards.map((item) => (
          <SmallMetric
            key={item.label}
            label={item.label}
            value={item.value}
            icon={item.icon}
          />
        ))}
      </div>

      {/* Sales charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <Panel
          title="Sales over time"
          subtitle="Last 7 days"
          className="lg:col-span-2"
        >
          {!hasSalesSeries ? (
            <EmptyChartState
              icon={TrendingUp}
              title="No sales data yet"
              subtitle="Sales from your orders will appear here automatically."
            />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart
                data={salesSeries}
                margin={{ left: 0, right: 8, top: 8, bottom: 0 }}
              >
                <defs>
                  <linearGradient
                    id="dashboardSalesGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor="#EA580C" stopOpacity={0.25} />

                    <stop offset="100%" stopColor="#EA580C" stopOpacity={0} />
                  </linearGradient>
                </defs>

                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#f0eeec"
                  vertical={false}
                />

                <XAxis
                  dataKey="label"
                  tick={{
                    fontSize: 12,
                    fill: "#78716C",
                  }}
                  axisLine={false}
                  tickLine={false}
                />

                <YAxis
                  tick={{
                    fontSize: 12,
                    fill: "#78716C",
                  }}
                  axisLine={false}
                  tickLine={false}
                  width={54}
                  domain={[0, "auto"]}
                  tickFormatter={(v) =>
                    `₹${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`
                  }
                />

                <Tooltip
                  formatter={(v) => [money(v), "Sales"]}
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid #E7E5E4",
                    fontSize: 13,
                  }}
                />

                <Area
                  type="monotone"
                  dataKey="sales"
                  stroke="#EA580C"
                  strokeWidth={2.5}
                  fill="url(#dashboardSalesGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Panel>

        {/* Sales by order type */}
        <Panel title="Sales by order type" subtitle="Today">
          {activeSalesTypes.length === 0 ? (
            <EmptyChartState
              icon={ShoppingBag}
              title="No orders yet"
              subtitle={
                isStarter
                  ? "Direct POS billing, takeaway and delivery sales will appear here."
                  : "Dine-in, takeaway and delivery sales will appear here."
              }
            />
          ) : (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={activeSalesTypes}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={55}
                    outerRadius={88}
                    paddingAngle={3}
                  >
                    {activeSalesTypes.map((e, i) => (
                      <Cell
                        key={e.name}
                        fill={CHART_COLORS[i % CHART_COLORS.length]}
                      />
                    ))}
                  </Pie>

                  <Tooltip
                    formatter={(v) => [money(v), "Sales"]}
                    contentStyle={{
                      borderRadius: 12,
                      fontSize: 13,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>

              <div className="flex flex-wrap gap-3 justify-center">
                {activeSalesTypes.map((e, i) => (
                  <div
                    key={e.name}
                    className="flex items-center gap-1.5 text-xs"
                  >
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{
                        background: CHART_COLORS[i % CHART_COLORS.length],
                      }}
                    />

                    {displayOrderType(e.name)}
                  </div>
                ))}
              </div>
            </>
          )}
        </Panel>
      </div>

      {/* Operational metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
        <Link to="/app/tables">
          <SmallMetric
            label="Active Tables"
            value={`${ops.active_tables || 0}/${ops.total_tables || 0}`}
            icon={LayoutGrid}
            className="h-full hover:border-primary transition-colors"
          />
        </Link>

        <Link to="/app/kitchen">
          <SmallMetric
            label="Pending KOTs"
            value={ops.pending_kots || 0}
            icon={ChefHat}
            className="h-full hover:border-primary transition-colors"
          />
        </Link>

        <Link to="/app/inventory">
          <SmallMetric
            label="Low Stock"
            value={ops.low_stock || 0}
            icon={AlertTriangle}
            className="h-full hover:border-primary transition-colors"
          />
        </Link>

        <Link to="/app/expenses">
          <SmallMetric
            label="Today Expenses"
            value={money(ops.today_expenses || 0)}
            icon={Wallet}
            className="h-full hover:border-primary transition-colors"
          />
        </Link>

        <Link to="/app/orders">
          <SmallMetric
            label="Pending Orders"
            value={k.pending_orders || 0}
            icon={ShoppingBag}
            className="h-full hover:border-primary transition-colors"
          />
        </Link>
      </div>

      {/* Top selling + Recent transactions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <Panel
          title="Top selling items"
          subtitle="Last 30 days"
          className="lg:col-span-2"
          action={
            hasTopItems && !isStarter ? (
              <Link
                to="/app/reports"
                className="text-xs font-semibold text-primary flex items-center gap-1 hover:underline"
              >
                View report
                <ArrowRight size={13} />
              </Link>
            ) : null
          }
        >
          {!hasTopItems ? (
            <EmptyChartState
              icon={ChefHat}
              title="No item sales yet"
              subtitle="Your best-selling menu items will appear here once orders are completed."
            />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={topItems}
                layout="vertical"
                margin={{
                  left: 40,
                  right: 16,
                  top: 5,
                  bottom: 5,
                }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#f0eeec"
                  horizontal={false}
                />

                <XAxis
                  type="number"
                  tick={{
                    fontSize: 12,
                    fill: "#78716C",
                  }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />

                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{
                    fontSize: 11,
                    fill: "#57534e",
                  }}
                  axisLine={false}
                  tickLine={false}
                  width={130}
                />

                <Tooltip
                  formatter={(v, n) =>
                    n === "qty"
                      ? [`${v} sold`, "Quantity"]
                      : [money(v), "Revenue"]
                  }
                  contentStyle={{
                    borderRadius: 12,
                    fontSize: 13,
                  }}
                />

                <Bar
                  dataKey="qty"
                  fill="#EA580C"
                  radius={[0, 6, 6, 0]}
                  barSize={16}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>

        {/* Recent transactions */}
        <Panel
          title="Recent transactions"
          action={
            recentTransactions.length > 0 ? (
              <Link
                to="/app/orders"
                className="text-xs font-semibold text-primary flex items-center gap-1 hover:underline"
              >
                View all
                <ArrowRight size={13} />
              </Link>
            ) : null
          }
        >
          {recentTransactions.length === 0 ? (
            <div className="min-h-[220px] flex flex-col items-center justify-center text-center">
              <div className="h-12 w-12 rounded-2xl bg-secondary grid place-items-center mb-3">
                <Receipt size={21} className="text-muted-foreground" />
              </div>

              <p className="font-semibold text-sm">No transactions yet</p>

              <p className="text-xs text-muted-foreground mt-1 max-w-[220px]">
                Completed orders and payments will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentTransactions.map((o) => (
                <div
                  key={o.id}
                  className="flex items-center justify-between py-2 border-b border-border last:border-0 gap-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">
                      #{o.order_no}
                      <span className="text-muted-foreground font-normal">
                        {" "}
                        · {displayOrderType(o.type)}
                      </span>
                    </p>

                    <p className="text-[11px] text-muted-foreground">
                      {timeAgo(o.created_at)}
                    </p>
                  </div>

                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold">{money(o.total || 0)}</p>

                    <StatusBadge status={o.payment_status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {/* Low stock alerts */}
      {lowStockItems.length > 0 && (
        <Panel
          title="Low stock alerts"
          subtitle="Items that need attention"
          className="mt-4"
          action={
            <Link
              to="/app/inventory"
              className="text-xs font-semibold text-primary flex items-center gap-1 hover:underline"
            >
              Manage inventory
              <ArrowRight size={13} />
            </Link>
          }
        >
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {lowStockItems.map((i) => (
              <div
                key={i.id}
                className="flex items-center justify-between rounded-xl bg-red-50/60 border border-red-100 px-3 py-2.5 gap-3"
              >
                <span className="text-sm font-medium truncate">{i.name}</span>

                <span className="text-xs font-bold text-red-600 whitespace-nowrap">
                  {i.current_stock} {i.unit}
                </span>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* Zero-data helpful footer */}
      {!hasSales && (
        <div className="mt-4 rounded-2xl border border-dashed border-border bg-secondary/20 px-5 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-sm">Your dashboard is ready</p>

              <p className="text-xs text-muted-foreground mt-1">
                Sales, takeaway, delivery customers and other business metrics
                will update automatically as orders are placed.
              </p>
            </div>

            <Link
              to="/app/orders"
              className="inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-xl bg-primary text-white text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              View Orders
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
